import React, { useReducer, useEffect, useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import { RenderList } from './components'
import pick from 'lodash/pick'
import sortBy from 'lodash/sortBy'
import intersection from 'lodash/intersection'
import { isObject } from 'lodash'

import {
  reducer as listViewReducerFn,
  initialState,
  actions
} from './listView.reducer'
import {
  ListViewDispatchContext,
  ListViewStateContext
} from './listView.context'

import debounce from 'lodash/debounce'
import EmployeeInitials from '@components/EmployeeInitials'
import Avatar from '@components/Avatar'

import * as MPActions from './components/actions'
import Tooltip from '../components/Tooltip/Tooltip'
import _ from 'lodash'
import ShareButton from './components/ShareButton'
import TooltipContent from './components/TooltipContent'
import ShareWithEmployeesModel from './components/ShareWithEmployeesModel'
import RemoveList from './components/RemoveList'
import SearchIcon from '@material-ui/icons/Search'
import { socket } from '../stores'
import CircularProgress from '@material-ui/core/CircularProgress'
import { exportDialogMessage } from '@atheneum/api/dist/lib'
import { Backdrop } from '@material-ui/core'
import ACL from '@components/ACL'
import { makeStyles } from '@material-ui/core/styles'
import { getStage, validate } from './components/actions/helper'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import { SherlockIgnoreModal } from './components/SherlockIgnoreModal'
import XLSImporter from './components/XLSImporter'
import { useUser } from '@hooks/user'
import {
  Select,
  FormControl,
  TextField,
  Snackbar,
  Button,
  ClickAwayListener,
  InputBase,
  Box,
  Typography,
  ButtonGroup,
  Paper,
  Popper,
  Grow,
  MenuItem,
  MenuList
} from '@material-ui/core'

import { massProcessingApi } from '@atheneum/api'
import { findOccInStatuses, ColumnActions } from './components/ColumnTitle'
import InfoIcon from '@material-ui/icons/Info'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import DownloadIcon from '@material-ui/icons/CloudDownload'

const isAddProject = () => {
  const hash = window.location.hash

  if (!hash) return null

  const match = hash.match(/addToProject=\d+/g)

  if (!match.length) return null

  const itemId = Number(match[0].replace('addToProject=', ''))

  window.location.hash = ''

  return itemId
}

async function loadListItems(listId, offset, dispatch) {
  const itemId = isAddProject()
  const res = await massProcessingApi.getListItems(listId, offset)
  dispatch({
    type: actions.GET_LISTITEMS_DONE,
    payload: { count: res.count, listItems: res.rows }
  })
  if (itemId) {
    return dispatch({ type: actions.ADD_TO_PROJECT, payload: { itemId } })
  } else {
    const newOffset = offset + 50
    if (res.count > newOffset) {
      return loadListItems(listId, newOffset, dispatch)
    }
  }
}

function ListView(props) {
  const [state, dispatch] = useReducer(listViewReducerFn, initialState)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => registerSocketHandler(dispatch, state), [])

  useEffect(() => {
    async function fetchList() {
      dispatch({ type: actions.GET_LIST, payload: props.id })
      try {
        const list = await massProcessingApi.getList(props.id)
        dispatch({ type: actions.GET_LIST_DONE, payload: list })
        await loadListItems(list.id, 0, dispatch)
      } catch (err) {
        if (err.response?.status === 403) {
          dispatch({
            type: actions.GET_LIST_ERROR,
            payload: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column'
                }}>
                <h2>You do not have permission to see this list</h2>
                <div>
                  Please ask the owner of the list to grant you access by share
                  functionality
                </div>
              </div>
            )
          })
        } else {
          dispatch({
            type: actions.GET_LIST_ERROR,
            payload: 'Error fetching list from server'
          })
        }
      }
    }
    fetchList(props.id)
    return undefined
  }, [props.id])

  if (state.loading)
    return (
      <Backdrop
        style={{
          zIndex: 999
        }}
        open={true}>
        <CircularProgress style={{ color: '#429bdc' }} />
      </Backdrop>
    )
  if (state.error) return <div>{state.error}</div>

  return (
    <>
      <ListViewDispatchContext.Provider value={dispatch}>
        <ListViewStateContext.Provider value={state}>
          <ListViewRender history={props.history} />
        </ListViewStateContext.Provider>
      </ListViewDispatchContext.Provider>
    </>
  )
}

function CircularProgressWithLabel({ value }) {
  return (
    <Box
      position="relative"
      style={{
        height: '30px'
      }}
      display="inline-flex">
      <CircularProgress
        style={{
          height: '30px',
          width: '30px'
        }}
      />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Typography variant="caption" component="p">
          {' '}
          <strong>{`${value}%`}</strong>{' '}
        </Typography>
      </Box>
    </Box>
  )
}

export default ListView
function ListViewRender({ history }) {
  const state = useContext(ListViewStateContext)
  const dispatch = useContext(ListViewDispatchContext)
  const DEBOUNCE_TIME = 500
  const isDisabled = !!state.massAction.stage

  const onNameChange = debounce(async name => {
    massProcessingApi.updateList({ id: state.list.id, data: { name } })
  }, DEBOUNCE_TIME)

  let items = filterItems(state)
  let syncItem = state.syncItem
  if (state.validationFilter) {
    if (!items.length) {
      dispatch({ type: actions.REMOVE_VALIDATION_FILTER })
    }
  }

  const listId = state.list && state.list.id
  useEffect(() => {
    ;(async () => {
      const getAllLists = await massProcessingApi.getLists()
      const currentListCount = getAllLists.find(list => list.id === listId)
        ?.noItems
      dispatch({
        type: actions.SET_NO_ITEMS,
        payload: parseInt(currentListCount)
      })
    })()
  }, [dispatch, listId])

  const [value] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [searchEmployees, setSearchEmployees] = useState(false)
  const [hoverEmployee, setHoverEmployee] = useState(0)
  const [openRemoveList, setOpenRemoveList] = useState(false)
  const [allSelected, setAllSelected] = useState(false)

  const handleDownloadList = async () => {
    if (!state.list) return
    exportDialogMessage({ exportType: 'Athena List' })
    dispatch({ type: actions.DOWNLOAD_STARTED })
    await massProcessingApi.exportList(state.list.id)
  }

  const handleOpenRemoveList = () => {
    setOpenRemoveList(true)
  }

  const handleCloseRemoveList = () => {
    setOpenRemoveList(false)
  }

  const handleHoverEmployee = id => setHoverEmployee(id)

  if (
    state.noItemsSet &&
    state.items.length === state.noItems &&
    !state.expertsLoaded
  ) {
    dispatch({ type: actions.SET_EXPERTS_LOADED })
  }

  const TooltipStatusContent = items => {
    const statusObj = findOccInStatuses(
      items.filter(item => !!item.data.status).map(item => item.data.status)
    )

    if (!statusObj.length) {
      return (
        <div>
          <p>No Statuses</p>
        </div>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          minWidth: '100px  '
        }}>
        <div>
          {statusObj.map((s, index) => (
            <p key={index}>{s.description}:</p>
          ))}
        </div>
        <div>
          {' '}
          {statusObj.map((s, index) => (
            <p key={index}>{s.occurrence}</p>
          ))}
        </div>
      </div>
    )
  }

  const [addingNewItem, setAddingNewItem] = useState(false)
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef(null)

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  // handle what happens on key press
  const handleKeyPress = async event => {
    if (event.keyCode === 13 && event.ctrlKey) {
      setAddingNewItem(true)
      dispatch({ type: actions.ADD_ITEM })
      const item = await massProcessingApi.createItem(
        state.listId,
        state.newItem.data
      )
      dispatch({ type: actions.ADD_ITEM_DONE, payload: item })
      setAddingNewItem(false)
    }
  }

  useEffect(() => {
    // attach the event listener
    document.addEventListener('keydown', handleKeyPress)

    // remove the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  return (
    <div className="listViewContainer">
      <h1
        style={{
          fontWeight: 100,
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center'
        }}>
        {' '}
        <Link to="/list" title="Back to Lists Overview">
          <ChevronLeftIcon style={{ height: '40px', width: '40px' }} />
        </Link>
        List Management
      </h1>
      <div className="listViewContainer-inner">
        <div className="titleListView">
          <div
            style={{
              position: 'absolute',
              top: '36px',
              left: '36px'
            }}>
            <InputBase
              placeholder="My List"
              defaultValue={state.name === 'My List' ? '' : state.name}
              inputProps={
                ({ 'aria-label': 'naked' },
                {
                  style: {
                    font: 'normal bold 16px Roboto',
                    letterSpacing: '0.12px',
                    color: '#429ADC',
                    padding: 0
                  }
                })
              }
              onChange={e => onNameChange(e.target.value)}
            />

            <div
              style={{ width: '250px', alignItems: 'center' }}
              className="mp-list-details">
              <p>
                {' '}
                Experts:{' '}
                {state.expertsLoaded && (
                  <strong>{state.items.length}</strong>
                )}{' '}
              </p>
              {!state.expertsLoaded && (
                <CircularProgressWithLabel
                  value={Math.round(
                    (state.items.length / state.noItems) * 100 || 0
                  )}
                />
              )}

              <p>
                Created on:{' '}
                <strong>
                  {new Intl.DateTimeFormat('en-GB')
                    .format(new Date(state.list.created_at))
                    .replaceAll('/', '-')}
                </strong>
              </p>
            </div>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                margin: '15px 0'
              }}>
              <SearchIcon
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 17,
                  height: 17,
                  color: '#9D9D9D'
                }}
              />
              <TextField
                style={{ width: '255px' }}
                placeholder="Search"
                InputLabelProps={{ shrink: false }}
                inputProps={{
                  style: {
                    padding: '0 0 3px 25px',
                    fontSize: '14px'
                  }
                }}
                value={state.filter}
                onChange={e =>
                  dispatch({
                    type: actions.SET_FILTER,
                    payload: e.target.value
                  })
                }
              />
              <ValidFilter />
              <StatusFilter />

              <Button
                onClick={() => markDuplicatesAsChecked(state.items, dispatch)}
                style={{
                  textTransform: 'capitalize',
                  padding: '1px',
                  color: '#33343A',
                  marginLeft: '15px'
                }}>
                Duplicates
              </Button>
              <Button
                onClick={() =>
                  toggleSelection(dispatch, allSelected, setAllSelected)
                }
                disabled={isDisabled}
                style={{
                  textTransform: 'capitalize',
                  padding: '1px',
                  color: '#33343A',
                  marginLeft: '15px'
                }}>
                {allSelected ? 'Unselect All' : 'Select All'}
              </Button>
            </div>
          </div>
          <div
            style={{
              position: 'absolute',
              top: '115px',
              right: '36px'
            }}>
            <span>
              <ColumnActions
                items={state.items}
                actions={{
                  createColumn: async data => {
                    const res = await massProcessingApi.updateList({
                      id: state.listId,
                      data: {
                        fields: [
                          ...state.list.fields,
                          {
                            ...data
                          }
                        ]
                      }
                    })
                    dispatch({ type: actions.ADD_COLUMN, payload: data })
                  },
                  editColumn: async () => {
                    dispatch({ type: actions.SET_EDIT_COLUMN, payload: true })
                  }
                }}
              />
            </span>
          </div>

          <div className="mass-processing-shareContainer">
            {Boolean(state.list?.sharedWith?.length) && !searchEmployees && (
              <div className="mass-processing-list-involvedEmployees">
                {state.list.sharedWith.map((employee, index) => {
                  const havePicture = !!employee.picture.length
                  if (index < 5) {
                    return havePicture ? (
                      <Tooltip
                        key={index}
                        tooltip={TooltipContent({ employeeData: employee })}>
                        <div>
                          <Avatar
                            className="mass-processing-list-userAvatar mass-processing-list-userAvatarSmall"
                            alt="User avatar"
                            src={employee.picture}
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <EmployeeInitials
                        key={index}
                        employee={{ ...employee, id: employee.userId }}
                      />
                    )
                  }
                  return null
                })}
                {state.list.sharedWith.length > 5 && (
                  <h5> + {state.list.sharedWith.length - 5} members</h5>
                )}
              </div>
            )}
            <ClickAwayListener onClickAway={() => setSearchEmployees(false)}>
              <div>
                <ShareButton
                  searchEmployees={searchEmployees}
                  setSearchEmployees={setSearchEmployees}
                />
                {searchEmployees ? (
                  <ShareWithEmployeesModel
                    dispatch={dispatch}
                    state={state}
                    value={value}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    hoverEmployee={hoverEmployee}
                    handleHoverEmployee={handleHoverEmployee}
                  />
                ) : null}
              </div>
            </ClickAwayListener>
            <Tooltip tooltip={TooltipStatusContent(state.items)}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  title: 'sort',
                  justifyContent: 'space-between',
                  paddingRight: '10px'
                }}>
                <InfoIcon style={{ fill: '#E5B71F' }}></InfoIcon>
                <span
                  style={{
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontWeight: 500
                  }}>
                  Status Info
                </span>
              </span>
            </Tooltip>
            <Button onClick={handleDownloadList}>
              {state.downloadInProgress ? (
                <CircularProgress
                  style={{
                    height: '17px',
                    width: '17px',
                    marginRight: '6px',
                    marginBottom: '4px'
                  }}
                />
              ) : (
                <>
                  <DownloadIcon />
                  <span
                    style={{
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 500,
                      textTransform: 'none',
                      paddingLeft: '2px'
                    }}>
                    Download
                  </span>
                </>
              )}
            </Button>
            <ButtonGroup
              variant="text"
              ref={anchorRef}
              aria-label="split button">
              <Button>
                <XLSImporter
                  addItem={async data => {
                    const item = await massProcessingApi.createItem(
                      state.listId,
                      data
                    )
                    dispatch({
                      type: actions.ADD_ITEM_DONE,
                      payload: item
                    })
                  }}
                />
              </Button>
              <Button
                size="small"
                aria-controls={open ? 'split-button-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-label="select merge strategy"
                aria-haspopup="menu"
                onClick={handleToggle}>
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom' ? 'center top' : 'center bottom'
                  }}>
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList id="split-button-menu" autoFocusItem>
                        <MenuItem onClick={event => {}}>
                          <a
                            href="https://s3.eu-central-1.amazonaws.com/atheneum-app.com-static-assets/demo-data/book-demo.csv"
                            download
                            style={{
                              color: '#33343A',
                              height: '25px',
                              width: '80px'
                            }}>
                            <span
                              style={{
                                fontFamily:
                                  '"Roboto", "Helvetica", "Arial", sans-serif',
                                fontWeight: 500
                              }}>
                              List Template
                            </span>
                          </a>
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            <RemoveList
              item={state.list}
              page="listView"
              openRemoveList={openRemoveList}
              handleOpenRemoveList={handleOpenRemoveList}
              handleCloseRemoveList={handleCloseRemoveList}
              history={history}
            />
            <SherlockIgnoreModal />
          </div>
        </div>
        {state.isFiltered && (
          <span
            style={{
              textDecoration: 'underline',
              color: '#429bdc',
              display: 'block',
              padding: '0 22px',
              position: 'absolute',
              top: '133px',
              right: 0,
              cursor: 'pointer'
            }}
            onClick={() =>
              dispatch({ type: actions.REMOVE_FILTER_ACTIVE_EXPERTS })
            }>
            View All
          </span>
        )}
        <div
          className="removeScrollBar"
          style={{
            width: '100%',
            height: 'calc(100% - 229px)',
            overflow: 'overlay'
          }}>
          <RenderList
            items={items}
            syncItem={syncItem}
            isDisabled={isDisabled}
          />
        </div>
        <div>
          <Snackbar
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            open={state.feedback}
            autoHideDuration={5000}
            onClose={() => dispatch({ type: actions.FEEDBACK_HIDE })}
            message={state.feedback}
          />
        </div>

        <div>
          <MassActions isDisabled={isDisabled} />
        </div>
        <div
          style={{
            justifyContent: 'end',
            padding: '10px'
          }}
          className="mass-processing--field-actions">
          {!addingNewItem ? (
            <>
              <AddCircleOutlineIcon
                title="Add Expert"
                className="al-addNewItem"
                style={{
                  zIndex: 9999
                }}
                onClick={async () => {
                  setAddingNewItem(true)
                  dispatch({ type: actions.ADD_ITEM })
                  const item = await massProcessingApi.createItem(
                    state.listId,
                    state.newItem.data
                  )
                  dispatch({ type: actions.ADD_ITEM_DONE, payload: item })
                  setAddingNewItem(false)
                }}
              />
              <span
                style={{
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  fontWeight: 500,
                  top: '8px',
                  position: 'relative'
                }}>
                Add Row
              </span>
            </>
          ) : (
            <CircularProgress
              style={{ color: '#429bdc', width: '30px', height: '30px' }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function MassActions({ isDisabled }) {
  const state = useContext(ListViewStateContext)
  const dispatch = useContext(ListViewDispatchContext)

  const user = useUser()

  const useStyles = makeStyles(() => ({
    select: {
      '&:focus': {
        background: 'none'
      },
      '&:before': {
        border: `1px solid ${isDisabled ? '#ccc' : '#429ADC'}`,
        height: '32px',
        borderRadius: '4px'
      }
    },
    icon: { color: '#429ADC' }
  }))
  const classes = useStyles()

  const renderActions = ({ survey }) => {
    return (
      <Select
        style={{ height: '32px', marginRight: '10px' }}
        className={classes.select}
        inputProps={{
          style: {
            color: `${isDisabled ? '#ccc' : '#429ADC'}`,
            padding: '0 10px'
          },
          classes: {
            icon: classes.icon,
            select: classes.select
          }
        }}
        native
        value={state.massAction?.name}
        onChange={e => {
          dispatch({
            type: actions.SELECT_ACTION,
            payload: e.target.value
          })
        }}>
        <option value="" disabled>
          Select action
        </option>
        {_.map(MPActions, action => {
          if (
            action.name === 'rapidSurvey' &&
            user &&
            !user.permissions.find(p =>
              ['RapidSurveyBasic', 'RapidSurveyAdmin'].includes(p.name)
            )
          ) {
            return null
          }

          return (
            <option key={action.name} value={action.name}>
              {action.title}
            </option>
          )
        })}
      </Select>
    )
  }

  return (
    <div className="mp-flex-row">
      <div className="surveyActionContainer">
        <FormControl style={{ width: 143 }} disabled={isDisabled}>
          <ACL
            roles={['RunSurvey']}
            alternative={renderActions({ survey: false })}>
            {renderActions({ survey: true })}
          </ACL>
        </FormControl>
        <RenderAction />
      </div>
    </div>
  )
}

function RenderAction() {
  // Decide what to render based on massAction name
  // massAction is changed by dispatch action to listView.reducer
  const state = useContext(ListViewStateContext)

  /**
   * Available mass actions
   *
   * @type { 'email' | 'copy' | 'rapidSurvey' | 'survey' | 'addToProject' }
   */
  const action = state.massAction?.name
  if (!action) return null

  const stage = MPActions[action].stages[state.massAction.stage]

  // stage example
  /*
  {
    component -> component to render
    full -> to display fullscreen or not
    validators -> validate data
  }
  */

  const Component = MPActions[action].component
  return (
    <div className={stage.full ? 'mass-processing--action-full' : ''}>
      <Component
        massAction={state.massAction}
        items={state.items.filter(i => i.data?.checked)}
        list={state.list}
      />
    </div>
  )
}

function registerSocketHandler(dispatch) {
  socket.on('mpItemUpdate', data => {
    console.debug('item sync', data)
    if (data.force || !data.ownEvent)
      dispatch({ type: actions.ITEM_SYNC, payload: data })
  })

  socket.on('athenaListExport', data => {
    if (data.isAthenaListDownload) {
      massProcessingApi.downloadList(data)
      dispatch({ type: actions.DOWNLOAD_COMPLETED })
    }
  })

  return () => {
    socket.off('mpItemUpdate')
    socket.off('athenaListExport')
  }
}

const StatusFilter = () => {
  const states = [
    { status: 'ALL', next: 'WITH_STATUS', label: 'With status' },
    { status: 'WITH_STATUS', next: 'WITHOUT_STATUS', label: 'Without status' },
    { status: 'WITHOUT_STATUS', next: 'ALL', label: 'All' }
  ]

  return (
    <FilterButton
      states={states}
      action={actions.FILTER_STATUSES}
      filter={'filterStatus'}
    />
  )
}

const ValidFilter = () => {
  const states = [
    { status: 'ALL', next: 'VALID', label: 'View Valid' },
    { status: 'VALID', next: 'INVALID', label: 'View Invalid' },
    { status: 'INVALID', next: 'ALL', label: 'View All' }
  ]

  return (
    <FilterButton
      states={states}
      action={actions.FILTER_VALIDS}
      filter={'filterValid'}
    />
  )
}

const FilterButton = ({ states, action, filter }) => {
  const state = useContext(ListViewStateContext)
  const dispatch = useContext(ListViewDispatchContext)
  const style = {
    textTransform: 'capitalize',
    padding: '1px',
    color: '#33343A',
    marginLeft: '15px'
  }

  return states.map(
    ({ status, next, label }, key) =>
      state[filter] === status && (
        <Button
          key={key}
          style={style}
          onClick={() => dispatch({ type: action, payload: next })}>
          {label}
        </Button>
      )
  )
}

function isSherlockAllowed(item, name) {
  const types = {
    email: 'consultation',
    survey: 'survey',
    copy: 'general'
  }
  const type = types[name] || 'general'
  return {
    allowed: item.data?.sherlock?.[type]?.allowed || false,
    success: item.data?.sherlock?.success
  }
}

export const filterItems = _.memoize(state => {
  const stage = getStage(state, MPActions)

  const filters = [
    {
      cond: s => s.filterStatus === 'WITH_STATUS',
      fn: (i, _s) => isObject(i.data?.status)
    },
    {
      cond: s => s.filterStatus === 'WITHOUT_STATUS',
      fn: (i, _s) => !isObject(i.data?.status)
    },
    {
      cond: s => Boolean(s.filter),
      fn: (i, s) => {
        const fields = s.fields.map(f => f.name)
        const data = pick(i.data, fields)
        return JSON.stringify(
          Object.values(data)
            .map(i => JSON.stringify(i))
            .join(' ') + (i.data?.status?.isError ? ' failed retry' : '')
        )
          ?.toLowerCase()
          .includes(s.filter.toLowerCase())
      }
    },
    {
      cond: s => s.filterValid === 'INVALID',
      fn: (i, s) => {
        return (
          !isValid(i, stage) ||
          !isSherlockAllowed(i, state.massAction.name).allowed
        )
      }
    },
    {
      cond: s => s.filterValid === 'VALID',
      fn: (i, s) => {
        return (
          isValid(i, stage) &&
          isSherlockAllowed(i, state.massAction.name).allowed
        )
      }
    }
  ]

  function isValid(item, stage) {
    if (!stage) return true
    return validate(item, stage.validators).isValid
  }

  const applicableFilters = filters.filter(f => f.cond(state))

  return state.items.filter(
    i => !_.find(applicableFilters, f => !f.fn(i, state))
  )
})

function markDuplicatesAsChecked(items, dispatch) {
  const orderedItems = sortBy(items, ['id']).map(item => {
    return {
      id: item.id,
      emails: [item.data.email, ...(item.data.emails || [])].filter(x => x)
    }
  })

  const idsOfDuplicates = orderedItems
    .filter(item => {
      const isDuplicate = orderedItems.find(
        compareItem =>
          compareItem.id > item.id &&
          intersection(item.emails, compareItem.emails).length
      )
      return isDuplicate
    })
    .map(i => i.id)

  return dispatch({ type: actions.BULK_CHECK, payload: idsOfDuplicates })
}

/**
 * Toggles selection of experts in the list
 */
function toggleSelection(dispatch, allSelected, setAllSelected) {
  setAllSelected(!allSelected)
  dispatch({ type: actions.TOGGLE_CHECKED, payload: !allSelected })
}
